import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "./ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import { Button } from "./ui/button";
import ArrowLeftIcon from "../assets/svgs/common/arrow-left.svg";
import { useNavigate } from "react-router-dom";
import Spinner from "./spinner.components";
import type React from "react";
import { useEffect, useState } from "react";

const FormSchema = z.object({
	pin: z.string().min(6, "Enter a valid OTP"),
});

interface AppProps {
	title: string;
	description: string;
	loading?: boolean;
	onResend: () => void;
	caller: (pin: string) => void;
	canResend: boolean;
	resendIn: number;
}

const OTPVerificationForm: React.FunctionComponent<AppProps> = ({
	title,
	description,
	loading,
	onResend,
	caller,
	canResend,
	resendIn,
}) => {
	const [time, setTime] = useState<number>(resendIn);

	const navigate = useNavigate();
	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			pin: "",
		},
	});

	function onSubmit(data: z.infer<typeof FormSchema>) {
		caller(data.pin);
	}

	function handleResend() {
		onResend();
		setTime(resendIn);
	}

	function ReturnTimer() {
		if (canResend) {
			return time === 0 ? (
				// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
				<p
					onClick={handleResend}
					className="text-primary underline cursor-pointer"
				>
					Resend code
				</p>
			) : (
				<p className="text-primary cursor-not-allowed">Resend code {time}</p>
			);
		}
		return null;
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (canResend && time > 0) {
			const interval = setInterval(() => {
				setTime((prev) => prev - 1);
			}, 1000);

			return () => clearInterval(interval);
		}
	}, [canResend, time, resendIn]);

	return (
		<div className="relative flex flex-col gap-10 w-full h-full">
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
			<div
				onClick={() => navigate(0)}
				className="-top-44 absolute md:flex items-center gap-2 hidden mb-16 font-semibold text-xl cursor-pointer"
			>
				<ArrowLeftIcon /> Back
			</div>
			<div className="w-full font-semibold">
				<h1 className="text-3xl">{title}</h1>
				<p className="text-lg text-secondary">{description}</p>
			</div>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-7 w-full"
				>
					<FormField
						control={form.control}
						name="pin"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<InputOTP maxLength={6} {...field}>
										<InputOTPGroup className="flex justify-between w-full">
											<InputOTPSlot index={0} />
											<InputOTPSlot index={1} />
											<InputOTPSlot index={2} />
											<InputOTPSlot index={3} />
											<InputOTPSlot index={4} />
											<InputOTPSlot index={5} />
										</InputOTPGroup>
									</InputOTP>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<div className="flex justify-between items-center w-full font-semibold">
						<p className="text-secondary">Didn't receive OTP?</p>
						<ReturnTimer />
					</div>

					<div className="flex flex-col items-center space-y-8">
						<Button
							className="border-primarybuttonborder bg-primarybuttonbg py-8 border w-full font-semibold text-lg text-primarybuttontext"
							type="submit"
							typeof="submit"
						>
							{loading ? <Spinner width={20} /> : "Verify"}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
};

export default OTPVerificationForm;
